const observerMap = new Map();

const RootIds = new WeakMap();
let rootId = 0;

let unsupportedValue = undefined;

export const defaultFallbackInView = (inView) => {
  unsupportedValue = inView;
}

const getRootId = (root) => {
  if (!root) return '0';
  if (RootIds.has(root)) return RootIds.get(root);
  rootId += 1;
  RootIds.set(root, rootId.toString());
  return RootIds.get(root);
}

export const optionsToId = (options) => {
  return Object.keys(options)
    .sort()
    .filter((key) => options[key] !== undefined)
    .map((key) => {
      return `${key}_${
        key === 'root' ? getRootId(options.root) : options[key]
      }`;
    })
    .toString();
}

const createObserver = (options) => {
  let id = optionsToId(options);
  let instance = observerMap.get(id);

  if (!instance) {
    const elements = new Map();
    let thresholds;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const inView =
          entry.isIntersecting &&
          thresholds.some((threshold) => entry.intersectionRatio >= threshold);

        if (options.trackVisibility && typeof entry.isVisible === 'undefined') {
          entry.isVisible = inView;
        }

        const els = elements.get(entry.target);

        if (els) {
          els.forEach((callback) => callback(inView, entry));
        }
      });
    }, options);

    thresholds =
      observer.thresholds ||
      (Array.isArray(options.threshold)
        ? options.threshold
        : [options.threshold || 0]);

    instance = {
      id,
      observer,
      elements,
    };

    observerMap.set(id, instance);
  }

  return instance;
}

export const observe = (
  element,
  callback,
  options = {},
  fallbackInView = unsupportedValue
) => {
  if (
    typeof window.IntersectionObserver === 'undefined' &&
    fallbackInView !== undefined
  ) {
    const bounds = element.getBoundingClientRect();
    callback(fallbackInView, {
      isIntersecting: fallbackInView,
      target: element,
      intersectionRatio:
        typeof options.threshold === 'number' ? options.threshold : 0,
      time: 0,
      boundingClientRect: bounds,
      intersectionRect: bounds,
      rootBounds: bounds,
    });
    return () => {};
  }

  const { id, observer, elements } = createObserver(options);

  let callbacks = elements.get(element) || [];
  if (!elements.has(element)) {
    elements.set(element, callbacks);
  }

  callbacks.push(callback);
  observer.observe(element);

  return function unobserve() {
    callbacks.splice(callbacks.indexOf(callback), 1);

    if (callbacks.length === 0) {
      elements.delete(element);
      observer.unobserve(element);
    }

    if (elements.size === 0) {
      observer.disconnect();
      observerMap.delete(id);
    }
  };
}
